import { useTranslation } from "react-i18next";
import { observer } from "mobx-react";
import { useTheme } from "@mui/material/styles";

import { isValue } from "@core/utils";

import { HighchartsBase } from "../../ui/analytics/charts";
import type { TooltipPointTypes } from "../../ui/analytics/charts/HighchartsBase/utils";
import { formatCompareDifference, formatTooltipValue } from "../../ui/analytics/charts/utils";

type SeriesCompareTypes = {
  seriesdata: { data: (number | null)[] }[];
  series: {
    color: string;
    label: string;
    name: string;
    type: string;
    unit: string;
  }[];
  yAxisOptions?: { label: () => string };
  width?: number | undefined;
  categories: string[];
  dense?: boolean | undefined;
  scaleYAxis?: boolean;
  yUnit: string;
};

function SeriesCompare({
  seriesdata,
  series,
  yAxisOptions = { label: () => "" },
  width,
  categories,
  dense = false,
  scaleYAxis = false,
  yUnit,
}: SeriesCompareTypes) {
  const theme = useTheme();
  const { t } = useTranslation(["_common"]);

  return (
    <HighchartsBase
      xAxis={{
        gridLineWidth: 0,
        lineWidth: 0,
        categories,
        labels: {
          formatter(this: { pos: number }) {
            const { pos } = this;
            return categories[pos];
          },
        },
      }}
      disableBoost
      yUnit={yUnit}
      yAxisOptions={{
        ...yAxisOptions,
        gridLineWidth: 0,
        lineWidth: 0,
        title: {
          text: "",
        },
      }}
      yAxisScale={scaleYAxis}
      chart={{
        height: dense ? 180 : 240,
        marginTop: 20,
        style: {
          marginBottom: "10px",
        },
        zoomType: "",
        width,
        backgroundColor: "white",
        boost: {
          seriesThreshold: 1000,
        },
      }}
      seriesOptions={{ marker: { enabled: false } }}
      tooltip={{
        shared: true,
        formatter() {
          const axisMax =
            this.points?.[0]?.series?.yAxis?.max ??
            this.points?.[0]?.series?.chart?.yAxis?.[0]?.max;
          const [point0, point1]: TooltipPointTypes[] = this.points;

          const createTooltipLine = (point: TooltipPointTypes, label: string) => {
            return `<span style="color:${point.color};">&#9679;&nbsp;${label} ${formatTooltipValue(point.y, yUnit, axisMax)} </span><br/>`;
          };

          let ttip = ``;

          if (point0 && point0.x === "◴") {
            ttip += `<span>&nbsp;&nbsp;&nbsp;◴ 12 ${t("text_months")}</span><br/>`;
          }

          if (point0 && isValue(point0.y)) {
            ttip += createTooltipLine(point0, point0.series.userOptions.label);
          }

          if (point1 && isValue(point1.y)) {
            ttip += createTooltipLine(point1, point1.series.userOptions.label);
          }

          if (point1 && point0 && isValue(point1.y) && isValue(point0.y)) {
            ttip += `<span>&nbsp;&nbsp;&nbsp;${t("text_difference")} ${formatCompareDifference(point0.y, point1.y, yUnit, axisMax)}</span>`;
          }

          return ttip;
        },
      }}
      legend={{ enabled: false }}
      series={[
        {
          ...series[0],
          ...seriesdata[0],
          color: theme.palette.graph.blue,
          showInLegend: true,
        },
        {
          ...series[1],
          ...seriesdata[1],
          color: theme.palette.graph.orange,
          lineWidth: 3,
          showInLegend: true,
        },
      ]}
    />
  );
}

export default observer(SeriesCompare);
